import { API_ENDPOINTS } from '@/constants';
import { normalizeContactInitWorkspace } from '@/utils/contactWorkspace';
import { getApiBaseUrl, getApiHeaders } from './headers';
import type {
  AddChatMessageNoteBody,
  AddTranscriptNoteBody,
  CallMetadataResponse,
  ChatInitResponse,
  GetTranscriptResponse,
  LinkCustomerToCallBody,
  LinkCustomerToCallResponse,
  RecordingControlResponse,
  StartCallMonitoringRequest,
  StartCallMonitoringResponse,
} from '@/types/call';
import type { ApiBuilder } from './types';

/**
 * Fetches chat init data for the given contact (GET, one-shot JSON).
 * Same conceptual payload as stream init: previousMessages, caller info, caseId.
 * `connectionToken` is the ChatJS participant connection token.
 */
export async function fetchChatInit(
  contactId: string,
  connectionToken: string
): Promise<ChatInitResponse> {
  const baseUrl = getApiBaseUrl();
  const params = new URLSearchParams({
    contactId,
    connectionToken,
  });
  const url = `${baseUrl}${API_ENDPOINTS.CALL.CHAT_INIT}?${params.toString()}`;
  const headers = await getApiHeaders();
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    throw new Error(`Chat init failed: ${res.status}`);
  }
  const data = (await res.json()) as ChatInitResponse;
  return normalizeContactInitWorkspace(data);
}

export type CallStreamMode = 'customer' | 'warm_consult';

export type FetchCallStreamOptions = {
  mode?: CallStreamMode;
  initialContactId?: string;
  warmTransferRequestId?: string | null;
  /** Outbound calls have no Boost metadata — ask the backend to skip it. */
  isOutbound?: boolean;
};

/**
 * Fetches the call stream (SSE) for the given contact.
 * Use this from callService; stream consumption (read/parse SSE, dispatch) stays there.
 */
export async function fetchCallStream(
  contactId: string,
  signal?: AbortSignal,
  options?: FetchCallStreamOptions
): Promise<Response> {
  const baseUrl = getApiBaseUrl();
  const params = new URLSearchParams({ contactId });
  if (options?.mode) params.set('mode', options.mode);
  if (options?.initialContactId) {
    params.set('initialContactId', options.initialContactId);
  }
  if (options?.warmTransferRequestId) {
    params.set('warmTransferRequestId', options.warmTransferRequestId);
  }
  // Send only 'true' or omit — any other value is treated as false.
  if (options?.isOutbound) params.set('isOutbound', 'true');
  const url = `${baseUrl}${API_ENDPOINTS.CALL.STREAM}?${params.toString()}`;
  const headers = await getApiHeaders({ eventStream: true });
  return fetch(url, {
    method: 'GET',
    headers,
    signal,
  });
}

/**
 * Fetches manager monitor stream (SSE) for the given contact.
 * Uses authenticated fetch streaming because native EventSource cannot send auth headers.
 */
export async function fetchManagerCallStream(
  contactId: string,
  signal?: AbortSignal,
  isOutbound?: boolean
): Promise<Response> {
  const baseUrl = getApiBaseUrl();
  // Send only 'true' or omit — any other value is treated as false.
  const outboundParam = isOutbound ? '&isOutbound=true' : '';
  const url = `${baseUrl}${API_ENDPOINTS.CALL.MANAGER_STREAM}?contactId=${encodeURIComponent(contactId)}${outboundParam}`;
  const headers = await getApiHeaders({ eventStream: true });
  return fetch(url, {
    method: 'GET',
    headers,
    signal,
  });
}

/**
 * Fetches /call/metadata for the given contact (GET, one-shot JSON).
 * When isOutbound is true the backend skips the Boost metadata fetch and
 * returns the minimal direct-connect payload ({ isDirectConnect, isCallback }).
 */
export async function fetchCallMetadata(
  contactId: string,
  isOutbound?: boolean
): Promise<CallMetadataResponse> {
  const baseUrl = getApiBaseUrl();
  // Send only 'true' or omit — any other value is treated as false.
  const query = isOutbound ? '?isOutbound=true' : '';
  const url = `${baseUrl}${API_ENDPOINTS.CALL.METADATA}/${encodeURIComponent(contactId)}${query}`;
  const headers = await getApiHeaders();
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    throw new Error(`Call metadata failed: ${res.status}`);
  }
  return res.json() as Promise<CallMetadataResponse>;
}

export interface AddTranscriptNoteResult {
  ok: boolean;
  status: number;
  errorMessage?: string;
}

async function postJsonExpect204(
  path: string,
  body: object
): Promise<AddTranscriptNoteResult> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${path}`;
  const headers = await getApiHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 204) return { ok: true, status: 204 };
  const errorMessage = await res.text();
  let message = errorMessage;
  try {
    const parsed = JSON.parse(errorMessage);
    if (parsed.message) message = parsed.message;
  } catch {
    // use raw text
  }
  return { ok: false, status: res.status, errorMessage: message };
}

/**
 * Adds a note to a transcript segment (live or stored).
 * Live: body must have contactId, note, sentenceId.
 * Stored: body must have contactId, note, transcriptId, segmentId.
 */
export async function addTranscriptNote(
  body: AddTranscriptNoteBody
): Promise<AddTranscriptNoteResult> {
  return postJsonExpect204(API_ENDPOINTS.CALL.TRANSCRIPT_NOTES, body);
}

/**
 * Adds a note to a chat message (POST /chat/messages/notes). Success: 204.
 */
export async function addChatMessageNote(
  body: AddChatMessageNoteBody
): Promise<AddTranscriptNoteResult> {
  return postJsonExpect204(API_ENDPOINTS.CALL.CHAT_MESSAGE_NOTES, body);
}

export async function pauseCallRecording(
  contactId: string
): Promise<RecordingControlResponse> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${API_ENDPOINTS.CALL.PAUSE}/${encodeURIComponent(contactId)}/pause`;
  const headers = await getApiHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Pause recording failed: ${res.status}`);
  }
  return res.json() as Promise<RecordingControlResponse>;
}

export async function resumeCallRecording(
  contactId: string
): Promise<RecordingControlResponse> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${API_ENDPOINTS.CALL.RESUME}/${encodeURIComponent(contactId)}/resume`;
  const headers = await getApiHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Resume recording failed: ${res.status}`);
  }
  return res.json() as Promise<RecordingControlResponse>;
}

export const callApi = (builder: ApiBuilder) => ({
  /**
   * Fetches stored transcript by contact ID (past call).
   */
  getTranscriptByContactId: builder.query<GetTranscriptResponse, string>({
    query: contactId => ({
      url: `${API_ENDPOINTS.CALL.TRANSCRIPTS}?contactId=${encodeURIComponent(contactId)}`,
      method: 'GET',
    }),
    providesTags: ['Call-Transcript'],
  }),

  /**
   * Updates the case ID for a specific call.
   */
  updateCallCaseId: builder.mutation<
    { success: boolean },
    { contactId: string; caseId: number; workspaceId: string }
  >({
    query: ({ contactId, caseId, workspaceId }) => ({
      url: `${API_ENDPOINTS.CALL.BY_CONTACT}/case-id`,
      method: 'PUT',
      body: { contactId, caseId, workspaceId },
    }),
    invalidatesTags: ['Call', 'Customer', 'Case'],
  }),
  /**
   * Links an active call to a workspace after direct-connect company selection.
   */
  linkCustomerToCall: builder.mutation<
    LinkCustomerToCallResponse,
    { contactId: string; body: LinkCustomerToCallBody }
  >({
    query: ({ contactId, body }) => ({
      url: `${API_ENDPOINTS.CALL.LINK_CUSTOMER}/${encodeURIComponent(contactId)}/link-customer`,
      method: 'PUT',
      body,
    }),
    invalidatesTags: ['Call', 'Case', 'Customer'],
  }),
  startCallMonitoring: builder.mutation<
    StartCallMonitoringResponse,
    StartCallMonitoringRequest
  >({
    query: ({ contactId, allowedMonitorCapabilities }) => ({
      url: API_ENDPOINTS.CALL.MONITOR,
      method: 'POST',
      body: {
        contactId,
        allowedMonitorCapabilities,
      },
    }),
  }),
});
